import Vue from 'vue';
import { debounce, indexOf, find } from 'lodash';
import VueRouter from 'vue-router';
const { isNavigationFailure, NavigationFailureType } = VueRouter;
import Dropdown from 'components/Butterfly/Dropdown/Dropdown.vue';
import DropdownItem from 'components/Butterfly/Dropdown/DropdownItem.vue';
import { BfModal, BfDialog, BfButton, BfSpinner } from 'components/Butterfly';
import Project from 'src/api/project';
import DataUtils from 'src/utils/data';
import store from 'src/store';
import { DELETE_PROJECT, FETCH_USER, UPDATE_PROJECT } from 'src/store/types';
import { mapGetters, mapActions } from 'vuex';
import FormatUtils from 'src/utils/formatters';
import ProjectLabelAdd from './ProjectLabels/ProjectLabelAdd.vue';
import ProjectLabelFilter from './ProjectLabels/ProjectLabelFilter.vue';
import ProjectLabel from './ProjectLabels/ProjectLabel.vue';
const PAGE_SIZE = 10;
const DEFAULT_SORT_BY = '-modified';
const SiteHome = Vue.extend({
    components: { Dropdown, DropdownItem, BfModal, BfDialog, BfButton, BfSpinner,
        ProjectLabelAdd, ProjectLabel, ProjectLabelFilter },
    metaInfo() {
        return {
            title: this.currentSite ? `${this.currentSite.site_name} - Kapiche` : 'Kapiche'
        };
    },
    async beforeRouteEnter(to, from, next) {
        await store.dispatch({ type: FETCH_USER });
        if (store.getters.currentUser?.viewer) {
            next({ name: 'viewer-home', params: { site: to.params.site } });
        }
        let pageNumber = to.query.page || 1;
        pageNumber = parseInt(pageNumber, 10);
        try {
            const response = await Project.getProjects(pageNumber, '', DEFAULT_SORT_BY);
            next(vm => {
                vm.data = response;
                vm.pageNumber = pageNumber;
            });
        }
        catch (error) {
            next();
        }
    },
    /**
     * This is used when changing between sites in the sidebar. beforeRouteEnter
     * doesn't work if only the "params" portion of the $router.push args
     * changes.
     */
    async beforeRouteUpdate(to, from, next) {
        // Only fetch new data if the site has changed
        if (to.params.site === from.params.site) {
            return next();
        }
        let pageNumber = to.query.page || 1;
        pageNumber = parseInt(pageNumber, 10);
        this.data = await Project.getProjects(pageNumber, '', this.sortBy);
        this.pageNumber = pageNumber;
        try {
            next();
        }
        catch (e) {
            if (!isNavigationFailure(e, NavigationFailureType.cancelled)) {
                throw e;
            }
        }
    },
    data() {
        return {
            data: {
                count: 0,
                results: null
            },
            filterName: '',
            filterLabels: [],
            isSearching: false,
            loading: false,
            labelsLoading: false,
            labelsOptions: [],
            selectedLabels: [],
            pageNumber: 1,
            sortBy: DEFAULT_SORT_BY,
            sortOptions: {
                "name": "Alphanumeric",
                "-created": "Date Created",
                "-modified": "Date Modified",
            },
            updatingProject: false,
            errorMessage: null,
            showingDeleteModal: false,
            selectedProject: null,
            hoveredProject: null
        };
    },
    computed: {
        ...mapGetters(['currentUser', 'currentSite', 'featureFlags']),
        firstResult() {
            return (this.pageNumber - 1) * PAGE_SIZE + 1;
        },
        pageCount() {
            return Math.ceil(this.data.count / PAGE_SIZE);
        },
        projects() {
            return this.data.results || [];
        },
        filtered() {
            return this.filterName.length > 0 || this.filterLabels.length > 0;
        },
    },
    methods: {
        ...mapActions({ UPDATE_PROJECT, DELETE_PROJECT }),
        number: FormatUtils.number,
        truncate: FormatUtils.truncate,
        pluraliseDesc: FormatUtils.pluraliseDesc,
        createdBy(project) {
            let user = project?.created_by;
            if (!user) {
                return '(missing)';
            }
            let name = user.first_name + ' ' + user.last_name;
            if (name.trim()) {
                return name;
            }
            else {
                return user.email;
            }
        },
        setSortBy(selected) {
            this.sortBy = selected;
            this.getProjects(1);
        },
        projectMenuCancelled() {
            this.selectedProject = null;
        },
        hoverRow(projectId) {
            if (this.selectedProject)
                return;
            this.hoveredProject = projectId;
        },
        unhoverRow() {
            if (this.selectedProject)
                return;
            this.hoveredProject = null;
            this.$refs['projectActionMenu'].forEach(menu => menu.isActive = false);
        },
        toggleModal(modal) {
            switch (modal) {
                case 'delete':
                    this.showingDeleteModal = show;
                    break;
            }
        },
        goToProject(projectId) {
            if (!this.hoveredProject)
                return;
            if (projectId !== this.hoveredProject)
                return;
            this.$router.push({
                name: 'project-details', params: { projectId: projectId, }
            });
        },
        clearNameFilter() {
            this.filterName = '';
            this.search();
        },
        async deleteProject(id) {
            this.updatingProject = true;
            this.errorMessage = null;
            try {
                await this.DELETE_PROJECT({ projectId: id });
                this.$analytics.track.project.delete(id);
                // wait for a moment so we don't get UI flicker if it happens too fast
                await setTimeout(async () => {
                    // now update project list page being shown
                    let refreshPageNumber = this.pageNumber;
                    // if the list that we were showing only had one in it, go to the previous page
                    if (this.projects.length === 1)
                        refreshPageNumber = Math.max(this.pageNumber - 1, 1);
                    await this.getProjects(refreshPageNumber);
                    this.showingDeleteModal = false;
                    this.updatingProject = false;
                    this.selectedProject = null;
                    this.hoveredProject = null;
                }, 2500);
            }
            catch (e) {
                this.errorMessage = e.userMsg;
            }
            finally {
                this.updatingProject = false;
            }
        },
        fmtDate: DataUtils.formatDate,
        async getProjects(pageNumber) {
            this.data = await Project.getProjects(pageNumber, this.filterName, this.sortBy, this.filterLabels);
            // Avoid pushing the same route
            if (this.pageNumber !== pageNumber) {
                this.pageNumber = pageNumber;
                this.$router.push({ name: 'home', query: { page: pageNumber } });
            }
            this.isSearching = false;
        },
        pageBack() {
            this.getProjects(this.pageNumber - 1);
        },
        pageForward() {
            this.getProjects(this.pageNumber + 1);
        },
        selectProject(id, name) {
            this.selectedProject = { id, name };
        },
        cancelProjectAction() {
            this.selectedProject = null;
            this.showingDeleteModal = false;
        },
        doProjectAction({ action, options }) {
            switch (action) {
                case 'delete':
                    this.selectedProject = options;
                    this.showingDeleteModal = true;
                    break;
                case 'rename':
                    this.$router.push({
                        name: 'project-edit',
                        params: { projectId: options.id }
                    });
                    break;
                default:
                    this.selectedProject = null;
            }
        },
        async deleteLabel(projectId, labelToDelete, currentLabels) {
            this.$analytics.track.labels.removeLabelFromProject();
            currentLabels.splice(indexOf(currentLabels, find(currentLabels, { id: labelToDelete })), 1);
            Project.updateProject(projectId, { labels: currentLabels });
        },
        async addLabel(projectId, labelToAdd, currentLabels) {
            this.$analytics.track.labels.addLabelToProject();
            currentLabels.push(labelToAdd);
            Project.updateProject(projectId, { labels: currentLabels });
        },
        handleLabelToggle(label) {
            const idx = this.filterLabels.indexOf(label);
            if (idx >= 0) {
                this.filterLabels.splice(idx, 1);
            }
            else {
                this.filterLabels.push(label);
            }
            this.$analytics.track.labels.filteredProjectList(this.filterLabels.length);
            this.search();
        },
        handleLabelReset() {
            this.filterLabels = [];
            this.search();
        },
        // Wrap the debounced search in an outer function
        // so that we can set isSearching flag without delay
        search: function () {
            this.isSearching = true;
            this.doSearch();
        },
        // Search, simply adds a debounce to projects fetching to handle continuous typing
        doSearch: debounce(function () {
            this.getProjects(1);
        }, 350)
    }
});
export default SiteHome;
